import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ChevronRight,
    Link2,
    Plus,
    X,
    ExternalLink,
    Loader2,
    RefreshCw,
    Globe,
    Copy,
    Trash2,
    CheckCircle,
    AlertCircle,
    Search,
    Users,
    Phone,
    Clock,
    Server,
    Activity,
    CheckSquare,
    Filter
} from 'lucide-react'
import { linksApi, templateApi, dialerApi, avatarApi, monitoringApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import './Links.css'

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.05 }
    }
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
    }
}

// Extract provider from URL (subdomain before common TLDs)
const getProvider = (url) => {
    try {
        let cleanUrl = url
        if (!cleanUrl.startsWith('http')) {
            cleanUrl = 'https://' + cleanUrl
        }
        const hostname = new URL(cleanUrl).hostname.replace('www.', '')

        // Common TLDs and domain patterns
        const tlds = ['.com', '.net', '.io', '.cc', '.tel', '.org', '.co', '.us', '.uk', '.ca', '.au']

        for (const tld of tlds) {
            const idx = hostname.indexOf(tld)
            if (idx > 0) {
                const beforeTld = hostname.substring(0, idx)
                const parts = beforeTld.split('.')
                return parts[parts.length - 1]
            }
        }

        return hostname.split('.')[0]
    } catch {
        return 'unknown'
    }
}

const getDomain = (url) => {
    try {
        let cleanUrl = url
        if (!cleanUrl.startsWith('http')) {
            cleanUrl = 'https://' + cleanUrl
        }
        return new URL(cleanUrl).hostname.replace('www.', '')
    } catch {
        return url
    }
}

function Links() {
    const navigate = useNavigate()
    const { user } = useAuth()

    // Data
    const [record, setRecord] = useState(null)
    const [links, setLinks] = useState([])
    const [templates, setTemplates] = useState([])
    const [dialers, setDialers] = useState([])
    const [avatars, setAvatars] = useState([])
    const [botStatus, setBotStatus] = useState([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)

    // Search & Filter
    const [searchQuery, setSearchQuery] = useState('')
    const [providerFilter, setProviderFilter] = useState('')

    // Selection
    const [selectedLinks, setSelectedLinks] = useState([])
    const [selectAll, setSelectAll] = useState(false)

    // Form
    const [newLink, setNewLink] = useState('')
    const [adding, setAdding] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)

    // Bulk actions
    const [bulkDeleting, setBulkDeleting] = useState(false)
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)

    // Toast notification
    const [toast, setToast] = useState(null)

    useEffect(() => {
        if (user && user.access_to_links !== true && user.access_to_links !== 1) {
            navigate('/')
            return
        }
        loadData()
    }, [user, navigate])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const loadData = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true)
        else setLoading(true)

        try {
            const [linksRes, templatesRes, dialersRes, avatarsRes, botStatusRes] = await Promise.allSettled([
                linksApi.getAll(),
                templateApi.getAll(),
                dialerApi.getAll(),
                avatarApi.getAll(),
                monitoringApi.getAllStatus()
            ])

            // Process links
            const linksData = linksRes.status === 'fulfilled' && Array.isArray(linksRes.value.data) ? linksRes.value.data : []
            if (linksData.length > 0) {
                const rec = linksData[0]
                setRecord(rec)
                try {
                    const parsedLinks = rec.links ? JSON.parse(rec.links) : []
                    setLinks(Array.isArray(parsedLinks) ? parsedLinks : [])
                } catch (e) {
                    console.error('Failed to parse links:', e)
                    setLinks([])
                }
            } else {
                setRecord(null)
                setLinks([])
            }

            setTemplates(templatesRes.status === 'fulfilled' ? (templatesRes.value.data || []) : [])
            setDialers(dialersRes.status === 'fulfilled' ? (dialersRes.value.data || []) : [])
            setAvatars(avatarsRes.status === 'fulfilled' ? (avatarsRes.value.data || []) : [])
            setBotStatus(botStatusRes.status === 'fulfilled' ? (botStatusRes.value.data || []) : [])

        } catch (error) {
            console.error('Failed to load data:', error)
            setLinks([])
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    // Lookup maps
    const avatarMap = useMemo(() => {
        const map = {}
        avatars.forEach(a => { map[a.id] = a })
        return map
    }, [avatars])

    const dialerMap = useMemo(() => {
        const map = {}
        dialers.forEach(d => { map[d.id] = d })
        return map
    }, [dialers])

    // Link associations
    const linkAssociations = useMemo(() => {
        const map = {}

        templates.forEach(template => {
            if (template.access_link) {
                const linkUrl = template.access_link.trim()
                if (!map[linkUrl]) {
                    map[linkUrl] = { templates: [], dialers: new Set(), avatars: new Set() }
                }
                map[linkUrl].templates.push(template)
                if (template.dialer_id) map[linkUrl].dialers.add(template.dialer_id)
                if (template.avatar_id) map[linkUrl].avatars.add(template.avatar_id)
            }
        })

        dialers.forEach(dialer => {
            if (dialer.access_link) {
                const linkUrl = dialer.access_link.trim()
                if (!map[linkUrl]) {
                    map[linkUrl] = { templates: [], dialers: new Set(), avatars: new Set() }
                }
                map[linkUrl].dialers.add(dialer.id)
                if (dialer.avatar_id) map[linkUrl].avatars.add(dialer.avatar_id)
            }
        })

        return map
    }, [templates, dialers])

    // Last used tracking
    const lastUsedMap = useMemo(() => {
        const dialerLastUsed = {}
        const avatarLastUsed = {}

        botStatus.forEach(bot => {
            const timestamp = bot.last_heartbeat || bot.updated_at || bot.last_seen
            if (timestamp) {
                const time = new Date(timestamp).getTime()
                if (bot.dialer_id) {
                    if (!dialerLastUsed[bot.dialer_id] || time > dialerLastUsed[bot.dialer_id]) {
                        dialerLastUsed[bot.dialer_id] = time
                    }
                }
                if (bot.avatar_id) {
                    if (!avatarLastUsed[bot.avatar_id] || time > avatarLastUsed[bot.avatar_id]) {
                        avatarLastUsed[bot.avatar_id] = time
                    }
                }
            }
        })

        return { dialerLastUsed, avatarLastUsed }
    }, [botStatus])

    // Stats
    const stats = useMemo(() => {
        const providerCounts = {}
        let activeCount = 0
        let linkedCount = 0

        links.forEach(link => {
            const provider = getProvider(link)
            providerCounts[provider] = (providerCounts[provider] || 0) + 1

            const assoc = linkAssociations[link]
            if (assoc && (assoc.dialers.size > 0 || assoc.avatars.size > 0)) {
                linkedCount++

                let isActive = false
                assoc.dialers.forEach(id => {
                    if (lastUsedMap.dialerLastUsed[id] && Date.now() - lastUsedMap.dialerLastUsed[id] < 24 * 60 * 60 * 1000) {
                        isActive = true
                    }
                })
                assoc.avatars.forEach(id => {
                    if (lastUsedMap.avatarLastUsed[id] && Date.now() - lastUsedMap.avatarLastUsed[id] < 24 * 60 * 60 * 1000) {
                        isActive = true
                    }
                })
                if (isActive) activeCount++
            }
        })

        const topProviders = Object.entries(providerCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)

        return {
            total: links.length,
            linked: linkedCount,
            unlinked: links.length - linkedCount,
            active: activeCount,
            providerCounts,
            topProviders,
            uniqueProviders: Object.keys(providerCounts).length
        }
    }, [links, linkAssociations, lastUsedMap])

    const allProviders = useMemo(() => Object.keys(stats.providerCounts).sort(), [stats.providerCounts])

    const getAssociations = (linkUrl) => {
        const assoc = linkAssociations[linkUrl]
        if (!assoc) return { dialers: [], avatars: [] }
        return {
            dialers: Array.from(assoc.dialers).map(id => dialerMap[id]).filter(Boolean),
            avatars: Array.from(assoc.avatars).map(id => avatarMap[id]).filter(Boolean)
        }
    }

    const getLastUsed = (linkUrl) => {
        const assoc = linkAssociations[linkUrl]
        if (!assoc) return null

        let latestTime = 0
        assoc.dialers.forEach(dialerId => {
            const time = lastUsedMap.dialerLastUsed[dialerId]
            if (time && time > latestTime) latestTime = time
        })
        assoc.avatars.forEach(avatarId => {
            const time = lastUsedMap.avatarLastUsed[avatarId]
            if (time && time > latestTime) latestTime = time
        })

        if (!latestTime) return null

        const diff = Date.now() - latestTime
        if (diff < 2 * 60 * 1000) return { text: 'Active', isActive: true }
        if (diff < 60 * 60 * 1000) return { text: `${Math.floor(diff / 60000)}m ago`, isActive: false }
        if (diff < 24 * 60 * 60 * 1000) return { text: `${Math.floor(diff / 3600000)}h ago`, isActive: false }
        if (diff < 7 * 24 * 60 * 60 * 1000) return { text: `${Math.floor(diff / 86400000)}d ago`, isActive: false }
        return { text: new Date(latestTime).toLocaleDateString(), isActive: false }
    }

    // Filter links
    const filteredLinks = useMemo(() => {
        return links
            .map((link, index) => ({ link, id: index + 1, originalIndex: index }))
            .filter(({ link, id }) => {
                if (providerFilter && getProvider(link) !== providerFilter) return false

                if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase().trim()
                    if (String(id).includes(query)) return true
                    if (link.toLowerCase().includes(query)) return true
                    const assoc = getAssociations(link)
                    if (assoc.dialers.some(d => d.name?.toLowerCase().includes(query))) return true
                    if (assoc.avatars.some(a => a.name?.toLowerCase().includes(query))) return true
                    return false
                }
                return true
            })
    }, [links, searchQuery, providerFilter, linkAssociations, dialerMap, avatarMap])

    // Selection handlers
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedLinks([])
            setSelectAll(false)
        } else {
            setSelectedLinks(filteredLinks.map(l => l.originalIndex))
            setSelectAll(true)
        }
    }

    const handleSelect = (originalIndex) => {
        setSelectedLinks(prev =>
            prev.includes(originalIndex)
                ? prev.filter(i => i !== originalIndex)
                : [...prev, originalIndex]
        )
        setSelectAll(false)
    }

    const clearSelection = () => {
        setSelectedLinks([])
        setSelectAll(false)
    }

    // CRUD operations
    const addLink = async () => {
        const link = newLink.trim()
        if (!link) return

        if (links.indexOf(link) !== -1) {
            showToast('This link already exists', 'error')
            return
        }

        setAdding(true)
        const updatedLinks = [...links, link]

        try {
            if (record) {
                await linksApi.update(record.id, {
                    id: record.id,
                    links: JSON.stringify(updatedLinks)
                })
            }
            setLinks(updatedLinks)
            setNewLink('')
            setShowAddForm(false)
            showToast('Link added successfully')
        } catch (error) {
            console.error('Failed to add link:', error)
            showToast('Failed to add link', 'error')
        } finally {
            setAdding(false)
        }
    }

    const removeLink = async (index) => {
        const updatedLinks = [...links]
        updatedLinks.splice(index, 1)

        try {
            await linksApi.update(record.id, {
                id: record.id,
                links: JSON.stringify(updatedLinks)
            })
            setLinks(updatedLinks)
            setSelectedLinks(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i))
            showToast('Link removed')
        } catch (error) {
            console.error('Failed to remove link:', error)
            showToast('Failed to remove link', 'error')
        }
    }

    const handleBulkDelete = async () => {
        if (selectedLinks.length === 0) return

        setBulkDeleting(true)
        try {
            const sortedIndices = [...selectedLinks].sort((a, b) => b - a)
            const updatedLinks = [...links]
            sortedIndices.forEach(idx => updatedLinks.splice(idx, 1))

            await linksApi.update(record.id, {
                id: record.id,
                links: JSON.stringify(updatedLinks)
            })

            setLinks(updatedLinks)
            setSelectedLinks([])
            setSelectAll(false)
            setShowBulkDeleteModal(false)
            showToast(`${selectedLinks.length} links deleted`)
        } catch (error) {
            console.error('Failed to bulk delete:', error)
            showToast('Failed to delete links', 'error')
        } finally {
            setBulkDeleting(false)
        }
    }

    const handleBulkCopy = () => {
        const selectedUrls = selectedLinks.map(idx => links[idx]).join('\n')
        navigator.clipboard.writeText(selectedUrls)
        showToast(`${selectedLinks.length} links copied`)
    }

    const copyLink = (link) => {
        navigator.clipboard.writeText(link)
        showToast('Link copied')
    }

    const openLink = (link) => {
        let url = link
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url
        }
        window.open(url, '_blank')
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && newLink.trim()) addLink()
        if (e.key === 'Escape') setShowAddForm(false)
    }

    return (
        <motion.div
            className="links-page"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Toast */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        className={`toast ${toast.type}`}
                        initial={{ opacity: 0, y: -20, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: -20, x: '-50%' }}
                    >
                        {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                        {toast.message}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Breadcrumb */}
            <motion.div className="breadcrumb" variants={itemVariants}>
                <Link to="/">Dashboard</Link>
                <ChevronRight size={16} />
                <span>Links</span>
            </motion.div>

            {/* Header */}
            <motion.div className="page-header" variants={itemVariants}>
                <div>
                    <h1 className="page-title">Access Links</h1>
                    <p className="page-subtitle">
                        {filteredLinks.length.toLocaleString()} of {links.length.toLocaleString()} links
                        {(searchQuery || providerFilter) && ' (filtered)'}
                    </p>
                </div>
                <div className="header-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => loadData(true)} disabled={refreshing}>
                        <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(true)}>
                        <Plus size={16} /> Add Link
                    </button>
                </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div className="stat-blocks" variants={itemVariants}>
                <div className="stat-block blue">
                    <div className="stat-block-icon"><Link2 size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{stats.total}</span>
                        <span className="stat-block-label">Total Links</span>
                    </div>
                </div>
                <div className="stat-block green">
                    <div className="stat-block-icon"><Activity size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{stats.active}</span>
                        <span className="stat-block-label">Active (24h)</span>
                    </div>
                </div>
                <div className="stat-block purple">
                    <div className="stat-block-icon"><Server size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{stats.uniqueProviders}</span>
                        <span className="stat-block-label">Providers</span>
                    </div>
                </div>
                <div className="stat-block orange">
                    <div className="stat-block-icon"><Users size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{stats.linked}</span>
                        <span className="stat-block-label">Linked</span>
                    </div>
                </div>
            </motion.div>

            {/* Provider Pills */}
            {stats.topProviders.length > 0 && (
                <motion.div className="provider-pills" variants={itemVariants}>
                    <span className="provider-label">Providers:</span>
                    {stats.topProviders.map(([provider, count]) => (
                        <button
                            key={provider}
                            className={`provider-pill ${providerFilter === provider ? 'active' : ''}`}
                            onClick={() => setProviderFilter(providerFilter === provider ? '' : provider)}
                        >
                            {provider} <span className="pill-count">{count}</span>
                        </button>
                    ))}
                    {providerFilter && (
                        <button className="provider-pill clear" onClick={() => setProviderFilter('')}>
                            <X size={12} /> Clear
                        </button>
                    )}
                </motion.div>
            )}

            {/* Toolbar */}
            <motion.div className="toolbar" variants={itemVariants}>
                <div className="toolbar-left">
                    <div className="search-box">
                        <Search size={16} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by ID, URL, dialer, avatar..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="search-clear" onClick={() => setSearchQuery('')}>
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <select
                        className="filter-select"
                        value={providerFilter}
                        onChange={(e) => setProviderFilter(e.target.value)}
                    >
                        <option value="">All Providers</option>
                        {allProviders.map(p => (
                            <option key={p} value={p}>{p} ({stats.providerCounts[p]})</option>
                        ))}
                    </select>
                </div>
            </motion.div>

            {/* Bulk Actions */}
            <AnimatePresence>
                {selectedLinks.length > 0 && (
                    <motion.div
                        className="bulk-bar"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                    >
                        <div className="bulk-info">
                            <CheckSquare size={16} />
                            <strong>{selectedLinks.length}</strong> selected
                            <button className="btn-link" onClick={clearSelection}>Clear</button>
                        </div>
                        <div className="bulk-actions">
                            <button className="btn btn-secondary btn-sm" onClick={handleBulkCopy}>
                                <Copy size={14} /> Copy
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => setShowBulkDeleteModal(true)}>
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Table */}
            <motion.div className="table-container" variants={itemVariants}>
                {loading ? (
                    <div className="empty-state">
                        <Loader2 size={32} className="spin" />
                        <p>Loading links...</p>
                    </div>
                ) : links.length === 0 ? (
                    <div className="empty-state">
                        <Link2 size={48} />
                        <h3>No links yet</h3>
                        <p>Add your first access link to get started</p>
                        <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
                            <Plus size={16} /> Add Link
                        </button>
                    </div>
                ) : filteredLinks.length === 0 ? (
                    <div className="empty-state">
                        <Search size={48} />
                        <h3>No matches</h3>
                        <p>Try adjusting your search or filters</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th className="col-checkbox">
                                    <input type="checkbox" checked={selectAll && filteredLinks.length > 0} onChange={handleSelectAll} />
                                </th>
                                <th className="col-id">ID</th>
                                <th className="col-provider">Provider</th>
                                <th className="col-link">Link</th>
                                <th className="col-assoc">Dialer / Avatar</th>
                                <th className="col-lastused">Last Used</th>
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLinks.map(({ link, id, originalIndex }) => {
                                const assoc = getAssociations(link)
                                const lastUsed = getLastUsed(link)
                                const provider = getProvider(link)

                                return (
                                    <tr key={`${link}-${id}`} className={selectedLinks.includes(originalIndex) ? 'selected' : ''}>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedLinks.includes(originalIndex)}
                                                onChange={() => handleSelect(originalIndex)}
                                            />
                                        </td>
                                        <td className="id-cell">{id}</td>
                                        <td><span className="provider-tag">{provider}</span></td>
                                        <td>
                                            <div className="link-cell">
                                                <span className="link-domain">{getDomain(link)}</span>
                                                <span className="link-path">{link.replace(/^https?:\/\/[^/]+/, '') || '/'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {assoc.dialers.length > 0 || assoc.avatars.length > 0 ? (
                                                <div className="assoc-row">
                                                    {assoc.dialers.slice(0, 1).map(d => (
                                                        <Link key={`d-${d.id}`} to={`/dialers/${d.id}`} className="assoc-chip dialer" onClick={(e) => e.stopPropagation()}>
                                                            <Phone size={10} />{d.name}
                                                        </Link>
                                                    ))}
                                                    {assoc.avatars.slice(0, 1).map(a => (
                                                        <Link key={`a-${a.id}`} to={`/avatars/${a.id}`} className="assoc-chip avatar" onClick={(e) => e.stopPropagation()}>
                                                            <Users size={10} />{a.name}
                                                        </Link>
                                                    ))}
                                                    {(assoc.dialers.length + assoc.avatars.length > 2) && (
                                                        <span className="assoc-more">+{assoc.dialers.length + assoc.avatars.length - 2}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td>
                                            {lastUsed ? (
                                                <span className={`last-used ${lastUsed.isActive ? 'active' : ''}`}>
                                                    {lastUsed.isActive && <span className="pulse-dot" />}
                                                    {lastUsed.text}
                                                </span>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="action-btn" onClick={() => openLink(link)} title="Open"><ExternalLink size={14} /></button>
                                                <button className="action-btn" onClick={() => copyLink(link)} title="Copy"><Copy size={14} /></button>
                                                <button className="action-btn danger" onClick={() => removeLink(originalIndex)} title="Delete"><Trash2 size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </motion.div>

            {/* Add Modal */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAddForm(false)}>
                        <motion.div className="modal" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title"><Plus size={20} /> Add New Link</h2>
                                <button className="modal-close" onClick={() => setShowAddForm(false)}><X size={20} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="input-group">
                                    <label className="input-label">URL</label>
                                    <input
                                        type="text"
                                        className="input"
                                        placeholder="https://example.dialerhosting.com/agc/vicidial.php"
                                        value={newLink}
                                        onChange={(e) => setNewLink(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={addLink} disabled={!newLink.trim() || adding}>
                                    {adding ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Add Link
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Modal */}
            <AnimatePresence>
                {showBulkDeleteModal && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}>
                        <motion.div className="modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title"><AlertCircle size={20} style={{ color: 'var(--danger)' }} /> Delete Links</h2>
                                <button className="modal-close" onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}><X size={20} /></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete <strong>{selectedLinks.length}</strong> links?</p>
                                <p className="text-muted">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleting}>Cancel</button>
                                <button className="btn btn-danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                                    {bulkDeleting ? <><Loader2 size={16} className="spin" /> Deleting...</> : <><Trash2 size={16} /> Delete {selectedLinks.length} Links</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

export default Links